import useSWR from "swr";
import { useAuthContext } from "src/auth/hooks";
import axios, { fetcher } from "src/utils/axios";
import type {
  MedicalCapabilities,
  MedicalDetail,
  MedicalPlayer,
  MedicalSettings,
  MedicalCategory,
  EligibilityResult,
} from "src/types/federation-medical";

export const medicalBase = (id: number) => `/providers/${id}/federation`;
export function useMedicalData<T>(
  id: number,
  path: string,
  enabled: boolean,
  params: Record<string, unknown> = {},
) {
  const { user } = useAuthContext();
  return useSWR<T>(
    enabled && id > 0 && user?.id
      ? [`${medicalBase(id)}/${path}#actor-${user.id}`, { params }]
      : null,
    fetcher,
    { keepPreviousData: false, shouldRetryOnError: false },
  );
}
export const useMedicalCapabilities = (
  id: number,
  enabled: boolean,
  federation?: number,
) =>
  useMedicalData<MedicalCapabilities>(
    id,
    "medical/capabilities",
    enabled,
    federation ? { federation_provider_id: federation } : {},
  );
export const useMedicalPlayers = (
  id: number,
  enabled: boolean,
  sport: string,
  workspace: number,
  state = "",
  offset = 0,
) =>
  useMedicalData<{ items: MedicalPlayer[]; total: number }>(
    id,
    "medical/players",
    enabled && !!sport,
    {
      sport,
      workspace_provider_id: workspace,
      offset,
      limit: 20,
      ...(state ? { state } : {}),
    },
  );
export const useMedicalDetail = (
  id: number,
  player: number,
  sport: string,
  enabled: boolean,
  workspace?: number,
) =>
  useMedicalData<MedicalDetail>(
    id,
    `medical/players/${player}`,
    enabled && player > 0 && !!sport,
    { sport, workspace_provider_id: workspace },
  );
export const useMedicalCategories = (
  id: number,
  enabled: boolean,
  workspace?: number,
) =>
  useMedicalData<{ items: MedicalCategory[] }>(
    id,
    "medical/categories",
    enabled,
    { workspace_provider_id: workspace },
  );
export const useMedicalSettings = (
  id: number,
  enabled: boolean,
  sport: string,
) =>
  useMedicalData<MedicalSettings>(id, "medical/settings", enabled && !!sport, {
    sport,
  });
export const useEligibility = (
  id: number,
  category: number,
  player: number,
  enabled: boolean,
  workspace?: number,
) =>
  useMedicalData<EligibilityResult>(
    id,
    `eligibility/categories/${category}/players/${player}`,
    enabled && category > 0 && player > 0,
    { workspace_provider_id: workspace },
  );
export const useMedicalMemberships = (workspace: number, enabled: boolean) =>
  useMedicalData<{ items: { id: number; label: string }[] }>(
    workspace,
    "medical-memberships",
    enabled,
  );
export function useOwnMedicalPlayers() {
  const { user } = useAuthContext();
  return useSWR<{
    items: { id: number; player_id: number; label: string; sports: string[] }[];
  }>(
    user?.id
      ? `/providers/federation-medical/my-players#actor-${user.id}`
      : null,
    fetcher,
    { keepPreviousData: false, shouldRetryOnError: false },
  );
}
export async function medicalCommand<T>(
  id: number,
  path: string,
  payload: Record<string, unknown>,
) {
  return (await axios.post<T>(`${medicalBase(id)}/${path}`, payload)).data;
}
export function medicalError(error: unknown) {
  const failure = error as {
    detail?: unknown;
    response?: { status?: number; data?: { detail?: unknown } };
  } | null;
  const detail = failure?.response?.data?.detail ?? failure?.detail;
  const status = failure?.response?.status;
  if (Array.isArray(detail) || status === 422) return "med_invalid";
  if (
    status === 503 ||
    (typeof detail === "string" &&
      (detail.includes("setup") ||
        detail.includes("capability_unavailable") ||
        detail.startsWith("Invalid provider timezone:")))
  )
    return "med_setup";
  if (
    status === 409 ||
    (typeof detail === "string" &&
      (detail.includes("conflict") || detail.includes("version")))
  )
    return "med_conflict";
  if (
    [401, 403, 404].includes(status || 0) ||
    (typeof detail === "string" &&
      /permission_denied|not_found|verified_account_required|exception_unavailable|decision_unavailable|medical_unavailable/.test(
        detail,
      ))
  )
    return "med_denied";
  if (
    typeof detail === "string" &&
    /^federation_medical_(future_verification|sport_unavailable|eligibility_blocked)/.test(
      detail,
    )
  )
    return "med_invalid";
  return "med_error";
}
